import React, { useCallback, useEffect, useState } from 'react';

import { makeStyles }  from '@material-ui/core/styles';
import Button          from '@material-ui/core/Button';
import Table           from '@material-ui/core/Table';
import TableBody       from '@material-ui/core/TableBody';
import TableCell       from '@material-ui/core/TableCell';
import TableContainer  from '@material-ui/core/TableContainer';
import TableHead       from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow        from '@material-ui/core/TableRow';
import Typography      from '@material-ui/core/Typography';

import LabelOutlinedIcon from '@material-ui/icons/LabelOutlined';

import {
  countByTicket,
  create,
  date,
} from '../util';
import NewPayment from './NewPayment';
import NewTickets from './NewTickets';

const style = {
  payments: makeStyles((theme) => ({
    title: {
      alignItems: 'flex-end',
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1),
      paddingBottom: theme.spacing(2),
    },
  })),
}

export default function Payments(props) {
  const classes = style.payments();

  const [ count, setCount ] = useState(0);
  const [ newPayment, setNewPayment ] = useState(create.payment());
  const [ newTickets, setNewTickets ] = useState([-1, 0]);
  const [ page, setPage ] = useState(0);
  const [ payments, setPayments ] = useState([]);

  const { request, ticketDetail } = props.context.getStates();

  const fetchPayments = useCallback((page) => {
    request.postSecurely('/attender/paid', {
      attender: props.attender.rowid, page,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else {
        if ('count' in response) {
          setCount(response.count);
        }
        if ('paidHistory' in response) {
          setPayments(response.paidHistory);
        }
      }
    });
  }, [request, props.attender.rowid]);

  const deletePayment = () => {
    request.postSecurely('/attender/paid/delete', {
      attender: props.attender.rowid,
      ...newPayment,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else {
        setNewPayment(create.payment());
        props.setRevision(props.revision + 1);
      }
    });
  }

  const insertPayment = (newRowid = undefined) => {
    request.postSecurely('/attender/paid/insert', {
      attender: props.attender.rowid,
      ...newPayment,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else if ('paid' in response) {
        setNewPayment((oldPayment) => ({...oldPayment, rowid: newRowid || response.paid}));
        props.setRevision(props.revision + 1);
      }
    });
  }

  const updatePayment = () => {
    request.postSecurely('/attender/paid/update', {
      ...newPayment,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else {
        setNewPayment(create.payment());
        props.setRevision(props.revision + 1);
      }
    });
  }

  const deleteTickets = (ticketRowid) => {
    request.postSecurely('/attender/issued/delete', {
      ...newPayment,
      ticketRowid,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else if ('issued_tickets' in response) {
        setNewPayment({...newPayment, issued: response.issued_tickets});
      }
    });
  }

  const insertTickets = () => {
    request.postSecurely('/attender/issued/insert', {
      ...newPayment,
      attender: props.attender.rowid,
      quantity: newTickets[1],
      ticketRowid: newTickets[0],
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else if ('issued_tickets' in response) {
        setNewPayment({...newPayment, issued: response.issued_tickets});
        setNewTickets([-1, 0]);
      }
    });
  }

  useEffect(() => {
    fetchPayments(page);
  }, [
    fetchPayments,
    page,
    props.revision,
  ]);

  return (
    <div>
      <NewPayment newPayment={newPayment}
        context={props.context}
        deletePayment={deletePayment}
        deleteTickets={deleteTickets}
        insertPayment={insertPayment}
        setNewPayment={setNewPayment}
        setNewTickets={setNewTickets}
        setRevision={props.setRevision}
        updatePayment={updatePayment}
      />

      <NewTickets newTickets={newTickets}
        context={props.context}
        insertTickets={insertTickets}
        newPayment={newPayment}
        setNewPayment={setNewPayment}
        setNewTickets={setNewTickets}
      />

      <div className={classes.title}>
        <Typography variant="h6"
          style={{marginBottom: 0}}
        >{'결제 내역'}</Typography>
        <Button color="primary"
          onClick={(event) => { setNewPayment(create.payment(-1)); }}
        >{'추가'}</Button>
      </div>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {['결제일', '결제 금액', '결제 방법', '이용권', '비고', ''].map((column, index) => (
                <TableCell key={index}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((paid) =>
              <TableRow hover key={paid.rowid}>
                <TableCell style={{whiteSpace: 'nowrap'}}>
                  {date.withoutTime(paid.date)}
                </TableCell>
                <TableCell>
                  {`${paid.amount.toLocaleString()} ${paid.currency}`}
                </TableCell>
                <TableCell>
                  {`${paid.method}`}
                </TableCell>
                <TableCell>
                  <table>
                    <tbody>
                      {Object.keys(ticketDetail).length > 0 && countByTicket(paid.issued).map((
                        ticketCounted, index
                      ) => (
                        <tr key={index}>
                          <td>
                            <LabelOutlinedIcon fontSize={'small'} style={{marginTop: 4}} />
                          </td>
                          <td style={{paddingLeft: '10px', paddingRight: '10px', whiteSpace: 'nowrap'}}>
                            <b>{ticketDetail[ticketCounted[0]].name}</b> &times; {ticketCounted[1]}장
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableCell>
                <TableCell>
                  {paid.note.split('\n').map((line, index) => (
                    <span key={index}>{line}<br/></span>
                  ))}
                </TableCell>
                <TableCell align={'right'}>
                  <Button onClick={(event) => { setNewPayment(paid); }}
                  >{'수정'}</Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination page={page} count={count}
        component="div"
        onPageChange={(event, newPage) => { setPage(newPage); }}
        rowsPerPage={5}
        rowsPerPageOptions={[]}
      />
    </div>
  );
}
